import React, { useState } from 'react';
import { 
  User, Settings, Crown, Star, Heart, Bell, Shield, LogOut, ChevronRight, Moon, Sun, 
  CreditCard, HelpCircle, Building2, Package, Store, X, Award, Clock, DollarSign,
  Storefront, ChevronDown, Globe, MapPin, Check, Briefcase, CalendarDays, Truck
} from 'lucide-react';
import { useApp, LOGOS } from '../context/AppContext';
import { PARTNER_PLANS, LOYALTY_PROGRAMS } from '../data/database';
import { useI18n, LanguageSelector } from '../i18n';

const ProfilePage = ({ onNavigate }) => {
  const { userMode, toggleUserMode, user, setUser, logout, isLoggedIn, selectedOffice, companyOffices, currentCompany, switchOffice } = useApp();
  const { t } = useI18n();
  const [darkMode, setDarkMode] = useState(false);
  const [showBecomePartner, setShowBecomePartner] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('start');
  const [showPartnerConfig, setShowPartnerConfig] = useState(false);
  const [showOfficeSwitcher, setShowOfficeSwitcher] = useState(false);

  // Get current Elit level info - L'utente parte senza livello (0 acquisti)
  const elitLevels = LOYALTY_PROGRAMS.elitUser.levels;
  const userPurchases = user.elitPurchases || 0;
  const currentElitLevel = elitLevels.find(l => userPurchases >= l.minPurchases) || { name: 'Base', discount: 0, level: 0 };
  const nextElitLevel = currentElitLevel.level > 0 
    ? elitLevels.find(l => l.level === currentElitLevel.level + 1)
    : elitLevels[0]; // Se non ha livello, il prossimo è Bronze
  const purchasesForNext = nextElitLevel ? nextElitLevel.minPurchases - userPurchases : 0;

  // Get current Star level info
  const starLevels = LOYALTY_PROGRAMS.star.levels;
  const userReferrals = user.starReferrals || 0;
  const currentStarLevel = starLevels.find(l => userReferrals >= l.minReferrals) || starLevels[0];

  // Consumer menu items
  const consumerMenuItems = [
    { id: 'favorites', icon: Heart, label: t('profile', 'favorites'), color: 'red', onClick: () => onNavigate('favorites') },
    { id: 'notifications', icon: Bell, label: t('profile', 'notifications'), color: 'amber', badge: user.notifications > 0 ? user.notifications : null, onClick: () => onNavigate('notifications') },
    { id: 'payments', icon: CreditCard, label: t('profile', 'paymentMethods'), color: 'green', onClick: () => onNavigate('payments') },
    { id: 'security', icon: Shield, label: t('profile', 'security'), color: 'slate' },
    { id: 'help', icon: HelpCircle, label: t('profile', 'helpCenter'), color: 'blue' }
  ];

  // Company configuration items (azienda generale)
  const companyConfigItems = [
    { id: 'company-settings', icon: Briefcase, label: 'Info Azienda', onClick: () => onNavigate('company-settings'), desc: 'Nome, logo, descrizione', bgClass: 'bg-slate-100', iconClass: 'text-slate-600' },
    { id: 'company-offices', icon: Building2, label: 'Gestione Sedi', onClick: () => onNavigate('company-offices'), desc: `${companyOffices?.length || 0} sedi configurate`, bgClass: 'bg-amber-100', iconClass: 'text-amber-600' },
  ];

  // Office configuration items (impostazioni specifiche per sede)
  const officeConfigItems = [
    { id: 'office-profile', icon: Store, label: 'Vetrina', onClick: () => onNavigate('office-profile'), desc: 'Copertina, info sede', bgClass: 'bg-emerald-100', iconClass: 'text-emerald-600' },
    { id: 'catalog', icon: Package, label: 'Catalogo', onClick: () => onNavigate('office-catalog'), desc: 'Servizi, prodotti, eventi', bgClass: 'bg-blue-100', iconClass: 'text-blue-600' },
    { id: 'operators', icon: User, label: 'Team', onClick: () => onNavigate('office-operators'), desc: 'Operatori e personale', bgClass: 'bg-purple-100', iconClass: 'text-purple-600' },
    { id: 'hours', icon: Clock, label: 'Orari', onClick: () => onNavigate('office-hours'), desc: 'Aperture e chiusure', bgClass: 'bg-orange-100', iconClass: 'text-orange-600' },
    { id: 'daily-pricing', icon: CalendarDays, label: 'Prezzi Giornalieri', onClick: () => onNavigate('daily-pricing'), desc: 'Noleggi, camere, barche', bgClass: 'bg-cyan-100', iconClass: 'text-cyan-600' },
    { id: 'shipping', icon: Truck, label: 'Spedizioni', onClick: () => onNavigate('shipping-config'), desc: 'Zone e costi consegna', bgClass: 'bg-rose-100', iconClass: 'text-rose-600' },
  ];

  // Partner menu items (senza Gestione Catalogo, ora è in Configurazione)
  const partnerMenuItems = [
    { id: 'notifications', icon: Bell, label: t('profile', 'notifications'), color: 'amber', badge: user.notifications > 0 ? user.notifications : null, onClick: () => onNavigate('notifications') },
    { id: 'payments', icon: CreditCard, label: t('profile', 'paymentMethods'), color: 'green', onClick: () => onNavigate('payments') },
    { id: 'security', icon: Shield, label: t('profile', 'security'), color: 'slate' },
    { id: 'help', icon: HelpCircle, label: t('profile', 'helpCenter'), color: 'blue' }
  ];

  const menuItems = userMode === 'partner' ? partnerMenuItems : consumerMenuItems;

  // Admin check
  const isAdmin = user?.role === 'admin';

  const handleBecomPartner = () => {
    setShowBecomePartner(false);
    setUser(prevUser => ({ ...prevUser, businessPlan: selectedPlan }));
    // Switch to partner mode directly
    toggleUserMode('partner');
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 pt-6 pb-8">
        <div className="max-w-screen-2xl mx-auto px-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              {user.avatar && user.avatar.startsWith('http') ? (
                <img 
                  src={user.avatar} 
                  alt={user.name}
                  className="w-20 h-20 rounded-2xl object-cover border-4 border-white/20"
                />
              ) : (
                <div className="w-20 h-20 rounded-2xl border-4 border-white/20 bg-white/20 backdrop-blur flex items-center justify-center text-white text-2xl font-bold">
                  {user.name?.split(' ').map(n => n[0]).join('') || 'U'}
                </div>
              )}
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-white">{user.name}</h1>
              <p className="text-emerald-100 text-sm">{user.email}</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="bg-white/20 backdrop-blur text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                  <Award className="w-3 h-3" />
                  Elit {currentElitLevel.name}
                </span>
                {user.isStarMember && (
                  <span className="bg-white/20 backdrop-blur text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    {currentStarLevel.name}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-6">
        {/* Mode Switcher */}
        <div className="-mt-4 relative z-10">
          <div className="bg-white rounded-2xl shadow-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${userMode === 'consumer' ? 'bg-emerald-100' : 'bg-blue-100'}`}>
                  {userMode === 'consumer' ? <User className="w-5 h-5 text-emerald-600" /> : <Store className="w-5 h-5 text-blue-600" />}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{userMode === 'consumer' ? t('profile', 'userMode') : t('profile', 'partnerMode')}</p>
                  <p className="text-sm text-gray-500">{userMode === 'consumer' ? t('profile', 'bookAndBuy') : t('profile', 'manageYourBusiness')}</p>
                </div>
              </div>
              {userMode === 'consumer' ? (
                <button 
                  onClick={() => setShowBecomePartner(true)}
                  className="bg-emerald-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-emerald-600 transition-colors"
                >
                  {t('profile', 'becomePartner')}
                </button>
              ) : (
                <button 
                  onClick={toggleUserMode}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  {t('profile', 'backToUser')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Program Cards (consumer mode) - Elit & Star side by side */}
        {userMode === 'consumer' && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            {/* Elit Program Card */}
            <div 
              onClick={() => onNavigate('elit')}
              className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-4 text-white cursor-pointer hover:shadow-lg transition-all"
            >
              <div className="flex items-center justify-between mb-2">
                <Award className="w-6 h-6" />
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Elit</span>
              </div>
              <p className="text-xl font-bold">{currentElitLevel.name}</p>
              <p className="text-amber-100 text-sm mb-3">Sconto {currentElitLevel.discount}%</p>
              <div className="flex items-center justify-between pt-2 border-t border-white/20">
                <span className="text-xs text-amber-100">{user.elitPurchases} acquisti</span>
                <ChevronRight className="w-4 h-4 text-amber-200" />
              </div>
            </div>

            {/* Star Program Card */}
            <div 
              onClick={() => onNavigate('star')}
              className="bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl p-4 text-white cursor-pointer hover:shadow-lg transition-all"
            >
              <div className="flex items-center justify-between mb-2">
                <Star className="w-6 h-6" />
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Star</span>
              </div>
              {user.isStarMember ? (
                <>
                  <p className="text-xl font-bold">€{(user.starEarnings || 0).toFixed(2)}</p>
                  <p className="text-purple-100 text-sm mb-3">Guadagni</p>
                  <div className="flex items-center justify-between pt-2 border-t border-white/20">
                    <span className="text-xs text-purple-100">{currentStarLevel.name}</span>
                    <ChevronRight className="w-4 h-4 text-purple-200" />
                  </div>
                </>
              ) : (
                <>
                  <p className="text-xl font-bold">Guadagna</p>
                  <p className="text-purple-100 text-sm mb-3">Invita amici</p>
                  <div className="flex items-center justify-between pt-2 border-t border-white/20">
                    <span className="text-xs text-purple-100">Attiva ora</span>
                    <ChevronRight className="w-4 h-4 text-purple-200" />
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Partner Info (partner mode) */}
        {userMode === 'partner' && (
          <div className="mt-4 space-y-3">
            {/* Business Card with Plan Info */}
            <div className="bg-white rounded-2xl shadow-sm p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Store className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{user.businessName || 'La tua attività'}</p>
                    <p className="text-sm text-gray-500">{t('profile', 'plan')} {user.businessPlan === 'start' ? 'Start' : 'Business'}</p>
                  </div>
                </div>
                {user.businessPlan === 'start' && (
                  <button className="text-emerald-600 text-sm font-medium">{t('profile', 'upgrade')}</button>
                )}
              </div>
            </div>

            {/* Configurazione Partner - Expandable Menu */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <button 
                onClick={() => setShowPartnerConfig(!showPartnerConfig)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                data-testid="partner-config-toggle"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                    <Settings className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Configurazione Partner</p>
                    <p className="text-sm text-gray-500">Gestisci la tua attività</p>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showPartnerConfig ? 'rotate-180' : ''}`} />
              </button>
              
              {showPartnerConfig && (
                <div className="border-t border-gray-100 bg-gray-50">
                  {/* Company Section */}
                  <div className="p-3 pb-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">Azienda</p>
                    <div className="grid grid-cols-2 gap-2">
                      {companyConfigItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <button
                            key={item.id}
                            onClick={item.onClick}
                            data-testid={`partner-config-${item.id}`}
                            className="flex flex-col items-center gap-2 p-3 bg-white rounded-xl hover:shadow-md transition-all text-center"
                          >
                            <div className={`w-10 h-10 ${item.bgClass} rounded-xl flex items-center justify-center`}>
                              <Icon className={`w-5 h-5 ${item.iconClass}`} />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{item.label}</p>
                              <p className="text-xs text-gray-500 line-clamp-1">{item.desc}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Office Section */}
                  <div className="p-3 pt-2 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-2 px-1">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Impostazioni Sede</p>
                      {companyOffices && companyOffices.length > 1 && (
                        <button
                          onClick={() => setShowOfficeSwitcher(!showOfficeSwitcher)}
                          className="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium"
                          data-testid="mobile-office-switcher"
                        >
                          <MapPin className="w-3 h-3" />
                          <span className="max-w-[80px] truncate">{selectedOffice?.name?.split(' - ').pop() || 'Sede'}</span>
                          <ChevronDown className={`w-3 h-3 transition-transform ${showOfficeSwitcher ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </div>
                    
                    {/* Office Switcher Dropdown */}
                    {showOfficeSwitcher && companyOffices && companyOffices.length > 1 && (
                      <div className="mb-3 bg-white rounded-xl border border-gray-200 overflow-hidden">
                        {companyOffices.map(office => (
                          <button
                            key={office.id}
                            onClick={() => { switchOffice(office.id); setShowOfficeSwitcher(false); }}
                            className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${office.id === selectedOffice?.id ? 'bg-emerald-50 text-emerald-700' : 'hover:bg-gray-50 text-gray-700'}`}
                          >
                            <MapPin className="w-4 h-4 flex-shrink-0" />
                            <span className="flex-1 truncate">{office.name}</span>
                            {office.id === selectedOffice?.id && <Check className="w-4 h-4 flex-shrink-0" />}
                          </button>
                        ))}
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-2">
                      {officeConfigItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <button
                            key={item.id}
                            onClick={item.onClick}
                            data-testid={`partner-config-${item.id}`}
                            className="flex flex-col items-center gap-2 p-3 bg-white rounded-xl hover:shadow-md transition-all text-center"
                          >
                            <div className={`w-10 h-10 ${item.bgClass} rounded-xl flex items-center justify-center`}>
                              <Icon className={`w-5 h-5 ${item.iconClass}`} />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{item.label}</p>
                              <p className="text-xs text-gray-500 line-clamp-1">{item.desc}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Elit Program for partners */}
            <div 
              onClick={() => onNavigate('elit')}
              className={`rounded-2xl p-4 cursor-pointer transition-all ${
                user.isElitPartner 
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white' 
                  : 'bg-white shadow-sm hover:shadow-md'
              }`}
              data-testid="partner-elit-program"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${user.isElitPartner ? 'bg-white/20' : 'bg-amber-100'}`}>
                    <Crown className={`w-6 h-6 ${user.isElitPartner ? 'text-white' : 'text-amber-600'}`} />
                  </div>
                  <div>
                    <p className={`font-medium ${user.isElitPartner ? 'text-white' : 'text-gray-900'}`}>{t('profile', 'elitProgram')}</p>
                    <p className={`text-sm ${user.isElitPartner ? 'text-amber-100' : 'text-gray-500'}`}>
                      {user.isElitPartner ? t('profile', 'active') : t('profile', 'increaseVisibility')}
                    </p>
                  </div>
                </div>
                <ChevronRight className={`w-5 h-5 ${user.isElitPartner ? 'text-amber-100' : 'text-gray-400'}`} />
              </div>
            </div>
          </div>
        )}

        {/* Admin Panel Access */}
        {isAdmin && (
          <div className="mt-6">
            <button
              data-testid="admin-panel-btn"
              onClick={() => onNavigate('admin')}
              className="w-full bg-gray-900 text-white rounded-2xl p-4 flex items-center gap-3 hover:bg-gray-800 transition-colors shadow-sm"
            >
              <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold">Pannello Amministrazione</p>
                <p className="text-xs text-gray-400">Gestione piattaforma, utenti, configurazioni</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        )}

        {/* Menu Items */}
        <div className="mt-6">
          <h3 className="text-sm font-medium text-gray-500 mb-3 px-1">{t('profile', 'settings')}</h3>
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {menuItems.map((item, i) => (
              <button
                key={item.id}
                onClick={item.onClick}
                className={`w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors ${
                  i < menuItems.length - 1 ? 'border-b border-gray-100' : ''
                }`}
              >
                <div className={`w-10 h-10 bg-${item.color}-100 rounded-xl flex items-center justify-center`}>
                  <item.icon className={`w-5 h-5 text-${item.color}-500`} />
                </div>
                <span className="flex-1 text-left font-medium text-gray-900">{item.label}</span>
                {item.badge && (
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                    typeof item.badge === 'number' 
                      ? 'bg-red-500 text-white' 
                      : item.badge === 'PRO' 
                        ? 'bg-emerald-100 text-emerald-600' 
                        : item.badge === t('profile', 'activate')
                          ? 'bg-amber-100 text-amber-600'
                          : 'bg-purple-100 text-purple-600'
                  }`}>
                    {typeof item.badge === 'number' && item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            ))}
          </div>
        </div>

        {/* Theme Toggle */}
        <div className="mt-6">
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {darkMode ? <Moon className="w-5 h-5 text-gray-500" /> : <Sun className="w-5 h-5 text-amber-500" />}
                <span className="font-medium text-gray-900">{t('profile', 'darkTheme')}</span>
              </div>
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`w-12 h-7 rounded-full transition-colors ${darkMode ? 'bg-emerald-500' : 'bg-gray-200'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform ml-1 mt-1 ${darkMode ? 'translate-x-5' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Language Selector - Card version for mobile */}
        <div className="mt-6">
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Globe className="w-5 h-5 text-blue-500" />
                <span className="font-medium text-gray-900">{t('profile', 'language') || 'Lingua'}</span>
              </div>
              <LanguageSelector />
            </div>
          </div>
        </div>

        {/* Logout */}
        <div className="mt-6">
          <button 
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 bg-red-50 text-red-600 p-4 rounded-2xl font-medium hover:bg-red-100 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            {t('profile', 'logout')}
          </button>
        </div>

        {/* Version & Logo */}
        <div className="mt-6 text-center pb-4">
          <img src={LOGOS.light} alt="Skipres" className="h-6 mx-auto mb-2 opacity-30" />
          <p className="text-gray-400 text-sm">{t('profile', 'version')} 2.0.0</p>
        </div>
      </div>

      {/* Become Partner Modal */}
      {showBecomePartner && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-3xl p-5 animate-slide-up max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">{t('profile', 'sellOnSkipres')}</h3>
              <button onClick={() => setShowBecomePartner(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 mb-6">{t('profile', 'choosePlan')}</p>

            <div className="space-y-3 mb-6">
              {PARTNER_PLANS.map((plan) => (
                <button
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan.id)}
                  className={`w-full p-4 rounded-2xl border-2 text-left transition-all relative ${
                    selectedPlan === plan.id 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {plan.popular && (
                    <span className="absolute -top-2 right-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      {t('profile', 'recommended')}
                    </span>
                  )}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-bold text-gray-900">{plan.name}</h4>
                      <p className="text-sm text-gray-500">{plan.description}</p>
                    </div>
                    <div className="text-right">
                      {plan.price === 0 ? (
                        <p className="text-lg font-bold text-emerald-600">{t('profile', 'free')}</p>
                      ) : (
                        <>
                          <p className="text-lg font-bold text-gray-900">€{plan.price}</p>
                          <p className="text-xs text-gray-500">/{plan.period}</p>
                        </>
                      )}
                    </div>
                  </div>
                  <ul className="space-y-1">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                    {plan.limitations?.map((limit, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-400">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        {limit}
                      </li>
                    ))}
                  </ul>
                </button>
              ))}
            </div>

            <button 
              onClick={handleBecomPartner}
              className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors"
            >
              {t('profile', 'continueWith')} {selectedPlan === 'start' ? 'Start' : 'Business'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
